import { Link } from 'react-router-dom';
import { Breadcrumb, Collapse, Layout, List, theme, Typography } from 'antd';

import { PageLayout } from '@app/web-components';

import { RunWorkflowButton } from '../run-workflow-button';

export const LandingPage = () => {
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const instructions = [
    {
      key: '1',
      label: 'Instructions',
      children: (
        <>
          <p>
            These workflow normally run automatically, but can be triggered manually to refresh/populate data in the
            system. Read the descriptions to understand what each workflow does. All can be run at any time without any
            issues.
          </p>
          <p>
            <strong>Developers:</strong>
            <br />
            <Typography.Text type="warning">
              Important! Be sure to have the svc-job-runner running to handle the request.
            </Typography.Text>
            <br />
            When running locally, you will need to run these workflows to populate the system with data.
            <ul>
              <li>
                <strong>Pull User Reference Data</strong> - Pull in all the users, departments and suppliers. The system
                expects this data to be loaded.
              </li>
            </ul>
          </p>
        </>
      ),
    },
  ];

  const getWorkflow = (workflowCommand: string) => ({
    workflowName: workflowCommand
      .split('.')[2]
      .replaceAll('_', ' ')
      .toLocaleLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase()),
    workflowCommand,
  });

  const workflows = [
    {
      ...getWorkflow('DOMAIN_AUTO.WORKFLOW.PULL_USER_REFERENCE_DATA'),
      toolTip: 'Pulls in all workers (as users), departments and suppliers. It takes about 1 minute to run.',
    },
    {
      ...getWorkflow('DOMAIN_ELIGIBILITY.WORKFLOW.PULL_ELIGIBILITIES_FOR_USERS_FACTORY'),
      toolTip:
        'Refreshes all subscription access eligibilities in the system. The factory will create multiple jobs to pull the data and run the validations.',
    },
    {
      ...getWorkflow('DOMAIN_AUTO.WORKFLOW.PULL_PRODUCT_FAMILY_HIERARCHIES'),
      toolTip: 'Updates all product families, their products, and dies in the system.',
    },
    {
      ...getWorkflow('DOMAIN_AUTO.WORKFLOW.PULL_PROCESS_PREFIX_DIES'),
      toolTip: 'Updates all dies with respect to the process prefixes tracked by saaf.',
    },
    {
      ...getWorkflow('DOMAIN_ACCESS.WORKFLOW.PULL_ACCESSES_FACTORY'),
      toolTip: 'Updates all accesses in the system. The factory will create multiple jobs to pull and update the data.',
    },
    {
      ...getWorkflow('DOMAIN_ACCESS.WORKFLOW.PULL_ACCESS_METAS'),
      toolTip: 'Updates all the access metas (extra information about the access) in the system.',
    },
    {
      ...getWorkflow('DOMAIN_ACCESS.WORKFLOW.PULL_ACCESS_MEMBERSHIPS_FACTORY'),
      toolTip:
        'Updates all access memberships (who has be granted the access) in the system. The factory will create multiple jobs to pull and update the data.',
    },
    {
      workflowName: 'Pull Access Requests Factory',
      workflowCommand: 'DOMAIN_SUBSCRIPTION_AUTO.WORKFLOW.PULL_ACCESS_REQUESTS_FACTORY',
      toolTip:
        'Updates all access requests in the system. The factory will create multiple jobs to pull and update the data.',
    },
    {
      workflowName: 'Pull Subscription Access Status Factory',
      workflowCommand: 'DOMAIN_SUBSCRIPTION_AUTO.WORKFLOW.PULL_SUBSCRIPTION_ACCESS_STATUS_FACTORY',
      toolTip:
        'Updates all subscription accesses status in the system. The factory will create multiple jobs to pull and update the data.',
    },
    {
      workflowName: 'Pull DMS Tickets',
      workflowCommand: 'DOMAIN_AUTO.WORKFLOW.PULL_DMS_TICKETS',
      toolTip: 'Updates all DMS tickets in the system.',
    },
    {
      workflowName: 'Pull ODC Approvals',
      workflowCommand: 'DOMAIN_AUTO.WORKFLOW.PULL_ODC_APPROVALS',
      toolTip: 'Pulls in all the ODC approvals',
    },
    {
      ...getWorkflow('DOMAIN_AUTO.WORKFLOW.PULL_VIRTUAL_LOCATIONS'),
      toolTip: 'Pulls in all the virtual locations',
    },
    {
      ...getWorkflow('DOMAIN_ACCESS.TASK_COMMAND.UPDATE_ALL_FACELESS_AUDIT_DEPENDENCY_RULES'),
      toolTip:
        'Ensures every access with faceless audit enabled is a dependency for the faceless account accesses for the faceless accounts that are members of the access with faceless audit enabled.',
    },
    {
      ...getWorkflow('DOMAIN_ACCESS_CASE_AUTO.TASK_COMMAND.OPEN_MISSING_ACCESS_DEPENDENCY_CASES'),
      toolTip: 'Opens access cases for access dependencies that are missing.',
    },
    {
      ...getWorkflow('DOMAIN_T3.WORKFLOW.GENERATE_EXTERNAL_SNAPSHOT'),
      toolTip: 'Generates an External Snapshot while expiring any active snapshots. Please use with Caution',
    },
  ];

  return (
    <>
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[
          { title: <Link to="/">Home</Link> },
          { title: 'Admin' },
          { title: 'Automation' },
          { title: 'Workflows' },
        ]}
      />
      <Layout.Content
        style={{
          padding: 24,
          margin: 0,
          minHeight: 280,
          background: colorBgContainer,
        }}
      >
        <PageLayout.Title title="Workflows" />
        <Collapse items={instructions} style={{ marginBottom: '1em' }} />
        <List
          bordered
          itemLayout="horizontal"
          dataSource={workflows}
          renderItem={(item, index) => (
            <List.Item>
              <List.Item.Meta
                avatar={
                  <RunWorkflowButton
                    key={index}
                    toolTip={item.toolTip}
                    workflowName={item.workflowName}
                    workflowCommand={item.workflowCommand}
                  />
                }
                title={item.workflowName}
                description={item.toolTip}
              />
            </List.Item>
          )}
        />
      </Layout.Content>
    </>
  );
};
